import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiMail, FiUser, FiPaperclip, FiSend, FiCheck, FiMessageCircle, FiMessageSquare, FiPenTool, FiEdit } from 'react-icons/fi';
import './SendEmail.css';

const SendEmail = () => {
  const [acceptedPapers, setAcceptedPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subject, setSubject] = useState('Congratulations on Your Paper Acceptance!');
  const [emailContent, setEmailContent] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [selectedPaper, setSelectedPaper] = useState(null);

  const mock_email = [
    "durjoyghosh328@gmail.com", "200120.cse@student.just.edu.bd", "alenrodri730@gmail.com", "petnestweb@gmail.com"
  ]

  useEffect(() => {
    const fetchAcceptedPapers = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-accepted-papers`
        );
        if (response.data?.data) {
          setAcceptedPapers(response.data.data);
          
          // Extract unique authors by email
          const allAuthors = response.data.data.flatMap(paper => 
            paper.author?.filter(author => author.email) || []
          );
          
          // Remove duplicates by email
          const uniqueAuthors = Array.from(
            new Map(allAuthors.map(author => [author.email, author])).values()
          );
          
          setRecipients(uniqueAuthors.map(author => ({
            id: author.email,
            name: `${author.firstName} ${author.lastName}`,
            email: author.email,
            selected: true
          })));
        } else {
          setError('Unexpected data format received from the server.');
        }
      } catch (error) {
        setError('Failed to fetch accepted papers. Please try again later.');
        console.error('Fetch papers error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAcceptedPapers();
  }, []);

  const toggleRecipient = (id) => {
    setRecipients(recipients.map(recipient => 
      recipient.id === id ? { ...recipient, selected: !recipient.selected } : recipient
    ));
  };

  const toggleSelectAll = () => {
    const allSelected = recipients.every(r => r.selected);
    setRecipients(recipients.map(r => ({ ...r, selected: !allSelected })));
  };

  const handleSendEmail = async () => {
    setSending(true);
    setSentSuccess(false);
    
    try {
      // Filter selected recipients
      const selectedRecipients = recipients.filter(r => r.selected);

      // recipients: [...mock_email]
      
      // Prepare data for the backend
      const emailData = {
        subject,
        content: emailContent,
        recipients: selectedRecipients.map(r => r.email),
      };
      
      // This would be replaced with your actual API call
      await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/send-email`,
        emailData
      );
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSentSuccess(true);
      setTimeout(() => setSentSuccess(false), 3000);
    } catch (error) {
      setError('Failed to send emails. Please try again later.');
      console.error('Email sending error:', error);
    } finally {
      setSending(false);
    }
  };

  const getEmailTemplate = () => {
    return `<p>Dear Author,</p>
<p>We are pleased to inform you that your paper <strong>"${selectedPaper?.title || '[Paper Title]'}"</strong> has been accepted for presentation at our conference.</p>
<p>This is a significant achievement, as we received a large number of high-quality submissions this year. The review committee was particularly impressed with the quality and relevance of your work.</p>
<p><strong>Next Steps:</strong></p>
<ul>
  <li>Please prepare your presentation according to the conference guidelines</li>
  <li>Submit your final camera-ready paper by [Date]</li>
  <li>Register for the conference by [Date] to secure your presentation slot</li>
</ul>
<p>We look forward to your participation in the conference and the opportunity to showcase your research to the international community.</p>
<p>Best regards,<br>
Conference Organizing Committee</p>`;
  };

  const applyTemplate = () => {
    setEmailContent(getEmailTemplate());
  };

  const getSelectedCount = () => recipients.filter(r => r.selected).length;

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'image'
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading accepted papers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <p className="error-message">{error}</p>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="email-notification-container">
      <div className="email-header">
        <h1><FiMail /> Send Emails</h1>
        <p>Send emails to authors of accepted papers</p>
      </div>

      <div className="email-content">
        <div className="recipient-section">
          <div className="section-header">
            <h2><FiUser /> Recipients</h2>
            <div className="recipient-count">
              {getSelectedCount()} of {recipients.length} authors selected
              <button 
                className="select-all-btn"
                onClick={toggleSelectAll}
              >
                {recipients.every(r => r.selected) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          </div>
          
          <div className="recipients-list">
            {recipients.map(recipient => (
              <div 
                key={recipient.id}
                className={`recipient-item ${recipient.selected ? 'selected' : ''}`}
                onClick={() => toggleRecipient(recipient.id)}
              >
                <div className="recipient-info">
                  <div className="recipient-name">{recipient.name}</div>
                  <div className="recipient-email">{recipient.email}</div>
                </div>
                <div className="recipient-checkbox">
                  {recipient.selected && <FiCheck />}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="compose-section">
          <div className="section-header">
            <h2><FiEdit className='penTool' />Compose Email</h2>
            <div className="template-actions">
              <button 
                className="preview-btn"
                onClick={() => setPreviewMode(!previewMode)}
              >
                {previewMode ? 'Edit Mode' : 'Preview Mode'}
              </button>
              <div className="paper-selector">
                <select 
                  value={selectedPaper?.paperID || ''}
                  onChange={(e) => setSelectedPaper(
                    acceptedPapers.find(p => p.paperID === parseInt(e.target.value)) || null
                  )}
                >
                  <option value="">Select paper for template (optional)</option>
                  {acceptedPapers.map(paper => (
                    <option key={paper.paperID} value={paper.paperID}>
                      {paper.title} (ID: {paper.paperID})
                    </option>
                  ))}
                </select>
                <button 
                  className="apply-template-btn"
                  onClick={applyTemplate}
                  disabled={!selectedPaper}
                >
                  <FiPaperclip /> Apply Template
                </button>
              </div>
            </div>
          </div>

          <div className="email-subject">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
            />
          </div>

          {previewMode ? (
            <div 
              className="email-preview"
              dangerouslySetInnerHTML={{ __html: emailContent || '<p>Compose your message...</p>' }}
            />
          ) : (
            <ReactQuill
              value={emailContent}
              onChange={setEmailContent}
              modules={modules}
              formats={formats}
              placeholder="Write your email content here..."
            />
          )}

          <div className="email-actions">
            <button 
              className="send-btn"
              onClick={handleSendEmail}
              disabled={sending || sentSuccess || getSelectedCount() === 0}
            >
              {sending ? (
                <span className="sending-spinner"></span>
              ) : sentSuccess ? (
                <><FiCheck /> Sent Successfully</>
              ) : (
                <><FiSend /> Send to {getSelectedCount()} Authors</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendEmail;